import D from "@/admin/dashboard/Dashboard.module.css";

import C from "@/admin/AdminTable.module.css";
import {
    type FailureDomainTolerance,
    type FailureResource,
    getFailureDomainTolerances,
} from "@/admin/dashboard/hostFailureTolerance.ts";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { adminAllHostsQueryAtom } from "@/admin/hosts/hostQuery.ts";
import { WAdminTable } from "@/admin/WAdminTable.tsx";
import { InfoTooltip, WTooltip } from "@/components/WTooltip.tsx";
import { ErrorFallback } from "@/modules/error/ErrorFallback.tsx";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { plural } from "@/utils/plural.tsx";
import { useAtomValue } from "jotai/react";
import { filter, map, partition, sortBy } from "remeda";

const resourceDisplay: Record<FailureResource, { label: string; unit: string }> = {
    vcpu: { label: "vCPU", unit: "vCPU" },
    vram: { label: "vRAM", unit: "GB" },
};
const failureResources = ["vcpu", "vram"] as const satisfies FailureResource[];

function formatPercent(value: number | null): string {
    return value == null ? "—" : `${value.toFixed(1)}%`;
}

function failureShortfall(domain: FailureDomainTolerance): string | null {
    const shortfalls = filter(
        map(failureResources, (resource) => {
            const additionalFree = domain.resources[resource].additionalFree;
            return additionalFree != null && additionalFree > 0
                ? `${Math.ceil(additionalFree)} ${resourceDisplay[resource].unit}`
                : null;
        }),
        (shortfall) => shortfall != null,
    );

    return shortfalls.length > 0 ? shortfalls.join(" · ") : null;
}

function FailureToleranceTooltip({ domain }: { domain: FailureDomainTolerance }) {
    const summary =
        domain.hosts === 0
            ? "Committed workloads would not fit after a single hypervisor failure."
            : `Any ${plural(domain.hosts, "hypervisor", "hypervisors")} can fail and all committed workloads still fit.`;

    return (
        <div>
            <div className="pb-0.5rem">{summary}</div>

            <table className="font-size-small tabular-nums">
                <thead>
                    <tr className="color-muted">
                        <th className="pr-1rem text-left">Resource</th>
                        <th className="px-0.5rem text-right">Free %</th>
                        <th className="px-0.5rem text-right">Largest hypervisor %</th>
                        <th className="pl-0.5rem text-right">Failures tolerated</th>
                    </tr>
                </thead>
                <tbody>
                    {map(failureResources, (resource) => {
                        const metrics = domain.resources[resource];
                        return (
                            <tr key={resource}>
                                <td>{resourceDisplay[resource].label}</td>
                                <td className="px-0.5rem text-right">{formatPercent(metrics.freePercent)}</td>
                                <td className="px-0.5rem text-right">{formatPercent(metrics.requiredFreePercent)}</td>
                                <td className="pl-0.5rem text-right">{metrics.tolerance}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function FailureTolerance({ domain }: { domain: FailureDomainTolerance }) {
    return (
        <WTooltip wide text={<FailureToleranceTooltip domain={domain} />}>
            <span
                className={`${domain.hosts > 0 ? "color-success" : "color-error"} underline decoration-dotted underline-offset-2`}
            >
                {domain.hosts}
            </span>
        </WTooltip>
    );
}

function FailureShortfall({ domain }: { domain: FailureDomainTolerance }) {
    if (domain.hosts > 0) return <>—</>;

    return <span className="color-error">{failureShortfall(domain) ?? "—"}</span>;
}

function HeaderTooltip({ children, text }: { children: string; text: string }) {
    return (
        <InfoTooltip wide text={<div>{text}</div>}>
            {children}
        </InfoTooltip>
    );
}

function FailureDomainsTable({
    domains,
    locationLabels,
}: {
    domains: FailureDomainTolerance[];
    locationLabels: Map<string, string>;
}) {
    return (
        <WAdminTable className="table-fixed">
            <colgroup>
                <col className="w-30rem" />
                <col className="w-7rem" />
                <col className="w-10rem" />
                <col className="w-13rem" />
                <col />
            </colgroup>
            <thead>
                <tr>
                    <th>Hypervisor pool</th>
                    <th className="text-right">
                        <HeaderTooltip text="Hypervisors accepting workloads that are not offline or in maintenance.">
                            In service
                        </HeaderTooltip>
                    </th>
                    <th className="text-right">
                        <HeaderTooltip text="How many hypervisors, in any combination, can fail with all committed workloads still fitting on the rest.">
                            Failures tolerated
                        </HeaderTooltip>
                    </th>
                    <th className="text-right">
                        <HeaderTooltip text="Capacity to free, or add, before this pool can lose one hypervisor with all committed workloads still fitting.">
                            Short for 1 failure
                        </HeaderTooltip>
                    </th>
                    <th className={C.Spacer} />
                </tr>
            </thead>
            <tbody>
                {domains.map((domain) => (
                    <tr key={`${domain.location}:${domain.poolUuid}`}>
                        <td>
                            <b>
                                {locationLabels.get(domain.location) ?? domain.location} · {domain.poolName}
                            </b>
                        </td>
                        <td className="text-right tabular-nums">
                            {domain.availableHosts} of {domain.totalHosts}
                        </td>
                        <td className="text-right tabular-nums">
                            <FailureTolerance domain={domain} />
                        </td>
                        <td className="text-right tabular-nums">
                            <FailureShortfall domain={domain} />
                        </td>
                        <td className={C.Spacer} />
                    </tr>
                ))}
            </tbody>
        </WAdminTable>
    );
}

function FailoverReadinessSectionContent() {
    const { error, data: hosts } = useAtomValue(adminAllHostsQueryAtom);
    const locations = usePricingLocations();
    const locationLabels = new Map(
        map(locations, (location): [string, string] => [
            location.slug,
            location.display_name === location.slug ? location.slug : `${location.display_name} (${location.slug})`,
        ]),
    );
    const [displayedDomains, hiddenDomains] = partition(
        getFailureDomainTolerances(hosts.values()),
        (domain) => domain.availableHosts > 0,
    );
    const domains = sortBy(
        displayedDomains,
        (domain) => domain.hosts,
        [(domain) => domain.utilization ?? Number.POSITIVE_INFINITY, "desc"],
        (domain) => domain.location,
        (domain) => domain.poolName,
    );
    const hiddenPools = hiddenDomains.length;
    const unprotectedDomains = filter(domains, (domain) => domain.hosts === 0).length;

    if (error && domains.length === 0) {
        return (
            <div className="h-83">
                <ErrorFallback error={error} />
            </div>
        );
    }

    return (
        <>
            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Failover Readiness</h2>
                    <p>Whether committed workloads still fit after hypervisor failures, least resilient pool first</p>
                </div>

                {domains.length > 0 && (
                    <div className={D.HeadingBlock}>
                        {unprotectedDomains > 0 ? (
                            <span className="color-error">
                                {unprotectedDomains} of {plural(domains.length, "pool", "pools")} cannot tolerate a
                                hypervisor failure
                            </span>
                        ) : domains.length === 1 ? (
                            <span className="color-success">1 pool tolerates a hypervisor failure</span>
                        ) : (
                            <span className="color-success">
                                All {plural(domains.length, "pool", "pools")} tolerate a hypervisor failure
                            </span>
                        )}
                        {hiddenPools > 0 && (
                            <p className="font-size-small color-muted">
                                {plural(hiddenPools, "pool", "pools")} with no hypervisors in service not shown
                            </p>
                        )}
                    </div>
                )}
            </div>

            {domains.length === 0 ? (
                <p className="color-muted">No hypervisors in service</p>
            ) : (
                <div className="w-full">
                    <FailureDomainsTable domains={domains} locationLabels={locationLabels} />
                    <p className="pt-0.5rem font-size-small color-muted">
                        A pool survives one failure when free capacity is at least the largest hypervisor&apos;s share.
                    </p>
                </div>
            )}
        </>
    );
}

export function FailoverReadinessSection() {
    return (
        <LightBlock className={D.Wide} loadingHeight="460px">
            <FailoverReadinessSectionContent />
        </LightBlock>
    );
}
